import {
  msPlanBulkEditFilterInterface,
  notesInterface,
  salesPlanBulkEditDataInterface,
  salesPlanBulkEditDataInterfaceDataInstanceInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { tmsScreenServices } from 'services/tmsScreenServices';
import { convertIsoToDateString } from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';

// Define sales plan type similar to market share type
type SalesPlanType = 'current' | 'old' | 'saved';

export const useSalesPlanBulkEdit = () => {
  const [editData, setEditData] = useState<salesPlanBulkEditDataInterface[] | []>([]);
  const [editDataCopy, setEditDataCopy] = useState<salesPlanBulkEditDataInterface[] | []>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [filterData, setFilterData] = useState<msPlanBulkEditFilterInterface>({
    Ramping: '',
    Concept: '',
    MoleculeAndDosageForm: '',
    StrategyCode: '',
    SellingCategory: '',
    GFC: '',
    ConceptSort: '',
    // COT: "",
  });
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [GFCNotesSet, setGFCNotesSet] = useState<Set<string>>(new Set());
  const [GFCEditedSet, setGFCEditedSet] = useState<Set<string>>(new Set());
  const [rowsWithoutSaveSalesPlan, setRowsWithoutSaveSalesPlan] = useState<Set<string>>(new Set());
  const [gfcCotSellingCategoryError, setgfcCotSellingCategoryError] = useState<Set<string>>(
    new Set()
  );
  const [gfcCotSellingCategorySaveLoading, setGfcCotSellingCategorySaveLoading] = useState<
    Set<string>
  >(new Set());
  const [isSaveAllLoading, setIsSaveloading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [nextPageLoading, setNextPageLoading] = useState<boolean>(false);

  // New states for accordion functionality
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Sales plan type selection state similar to market share type selection
  const [salesPlanTypeSelection, setSalesPlanTypeSelection] = useState<Map<string, SalesPlanType>>(
    new Map()
  );

  //   states for notes
  const [isNotesLoading, setIsNotesLoading] = useState<boolean>(false);
  const [notesData, setNotesData] = useState<notesInterface[]>();
  const [isNoteSaving, setIsNoteSaving] = useState<boolean>(false);
  const [isNoteEdited, setIsNoteEdited] = useState<Map<number, boolean>>(new Map());
  const [isNoteDeleted, setIsNoteDeleted] = useState<Map<number, boolean>>(new Map());

  const [_savingGFCCotSC, _setSavingGFCCotSC] = useState<Set<string>>(new Set());
  const [singleSaveConfirmationModalVisibility, setSingleSaveConfirmationModalVisibility] =
    useState<boolean>(false);
  const [saveAllConfirmationModalVisibility, setSaveAllConfirmationModalVisibility] =
    useState<boolean>(false);
  const [isRouteChangeConfirmationPopUpVisible, setIsRouteChangeConfirmationPopUpVisible] =
    useState<boolean>(false);
  const [isCancelConfirmationPopVisible, setisCancelConfirmationPopVisible] =
    useState<boolean>(false);
  const [focussedRowForNoteAdd, setFocussedRowForNoteAdd] = useState<null | number>(null);
  const [focussedRowForSaving, setFocussedRowForSaving] = useState<null | number>(null);
  const [deletingNote, setDeletingNote] = useState<notesInterface | null>(null);
  const [isNoteDeleting, setIsNoteDeleting] = useState<boolean>(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState<boolean>(false);
  const [_latestNote, setLatestNote] = useState<notesInterface>();
  const navigate = useNavigate();

  // Helper function to generate GFC-COT key for type selection
  const getGFCCOTKey = (rowIndex: number): string => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return '';
    }
    const rowData = editData[rowIndex];
    return `${rowData.GFC}-${rowData.COT}-${rowData.SellingCategory}`;
  };

  // Get available sales plan types for a row
  const getAvailableSalesPlanTypes = (rowIndex: number): SalesPlanType[] => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return ['current'];
    }
    const rowData = editData[rowIndex];
    const availableTypes: SalesPlanType[] = ['current']; // Current is always available

    // Check if any month has saved values
    const hasSavedValues = rowData.data.some(item => item.isSavedSalesAvailable);
    if (hasSavedValues) {
      availableTypes.push('saved');
    }

    // Check if any month has old values
    const hasOldValues = rowData.data.some(item => item.isOldSalesAvailable);
    if (hasOldValues) {
      availableTypes.push('old');
    }

    return availableTypes;
  };

  // Get default sales plan type for a row
  const getDefaultSalesPlanType = (rowIndex: number): SalesPlanType => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return 'current';
    }
    const rowData = editData[rowIndex];

    // Check if any month has saved values
    const hasSavedValues = rowData.data.some(item => item.isSavedSalesAvailable);
    if (hasSavedValues) {
      return 'saved'; // Saved has highest priority
    }

    // Check if any month has old values
    const hasOldValues = rowData.data.some(item => item.isOldSalesAvailable);
    if (hasOldValues) {
      return 'old'; // Old has second priority
    }

    return 'current'; // Current is default fallback
  };

  // Check if sales plan type is changeable for a row
  const isSalesPlanTypeChangeable = (rowIndex: number): boolean => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return false;
    }
    const rowData = editData[rowIndex];

    // Show toggle whenever old values are available, regardless of saved values
    return rowData.data.some(item => item.isOldSalesAvailable);
  };

  // Initialize sales plan type for a row
  const initializeSalesPlanTypeForRow = (rowIndex: number) => {
    const gfcCotKey = getGFCCOTKey(rowIndex);

    // Only set if not already set
    if (!salesPlanTypeSelection.has(gfcCotKey)) {
      const defaultType = getDefaultSalesPlanType(rowIndex);
      setSalesPlanTypeSelection(prev => {
        const newMap = new Map(prev);
        newMap.set(gfcCotKey, defaultType);
        return newMap;
      });
    }
  };

  // Get selected sales plan type for a row
  const getSelectedSalesPlanType = (rowIndex: number): SalesPlanType => {
    const gfcCotKey = getGFCCOTKey(rowIndex);

    // Initialize if not set
    if (!salesPlanTypeSelection.has(gfcCotKey)) {
      const defaultType = getDefaultSalesPlanType(rowIndex);
      setSalesPlanTypeSelection(prev => {
        const newMap = new Map(prev);
        newMap.set(gfcCotKey, defaultType);
        return newMap;
      });
      return defaultType;
    }

    return salesPlanTypeSelection.get(gfcCotKey) || getDefaultSalesPlanType(rowIndex);
  };

  // Handle sales plan type change
  const handleSalesPlanTypeChange = (rowIndex: number, type: SalesPlanType) => {
    const gfcCotKey = getGFCCOTKey(rowIndex);
    const defaultType = getDefaultSalesPlanType(rowIndex);
    const rowData = editData[rowIndex];
    const rowKey = `${rowData.GFC}-${rowData.COT}-${rowData.SellingCategory}`;

    if (type !== defaultType) {
      setGFCEditedSet(prev => {
        const next = new Set(prev);
        next.add(rowKey);
        return next;
      });
    } else {
      // Reverting to default — only clear edited flag if values also match the copy
      const currentData = rowData.data;
      const copyData = editDataCopy[rowIndex].data;
      let hasValueEdits = false;
      for (let i = 0; i < currentData.length; i++) {
        switch (type) {
          case 'current':
            hasValueEdits =
              currentData[i].AdjustedMarketForecast.toString() !==
              copyData[i].AdjustedMarketForecast.toString();
            break;
          case 'old':
            hasValueEdits = currentData[i].OldAdjustments !== copyData[i].OldAdjustments;
            break;
          case 'saved':
            hasValueEdits = currentData[i].SavedAdjustments !== copyData[i].SavedAdjustments;
            break;
        }
        if (hasValueEdits) break;
      }
      if (!hasValueEdits) {
        setGFCEditedSet(prev => {
          const next = new Set(prev);
          next.delete(rowKey);
          return next;
        });
      }
    }

    setSalesPlanTypeSelection(prev => {
      const newMap = new Map(prev);
      newMap.set(gfcCotKey, type);
      return newMap;
    });
  };

  // Get sales plan value by type
  const getSalesPlanValueByType = (
    rowIndex: number,
    monthDateString: string,
    type: SalesPlanType
  ): string => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return '-';
    }
    const data = editData[rowIndex].data;
    for (let i = 0; i < data.length; i++) {
      if (data[i].PlanningMonth === monthDateString) {
        switch (type) {
          case 'current':
            return data[i].AdjustedMarketForecast.toLocaleString('en-US');
          case 'old':
            return data[i].isOldSalesAvailable ? data[i].OldAdjustments.toLocaleString('en-US') : '-';
          case 'saved':
            return data[i].isSavedSalesAvailable ? data[i].SavedAdjustments.toLocaleString('en-US') : '-';
          default:
            return data[i].AdjustedMarketForecast.toLocaleString('en-US');
        }
      }
    }
    return '-';
  };

  // Check if should show toggle for sales plan type
  const shouldShowToggle = (rowIndex: number): boolean => {
    return isSalesPlanTypeChangeable(rowIndex);
  };

  const getNotesData = async (gfc: number, SellingCategory: string) => {
    try {
      setIsNotesLoading(true);
      const data = await tmsScreenServices.getGFCNotes(gfc, SellingCategory);
      if (data) {
        setNotesData(data);
      }
    } catch (_error) {
      console.log('Error while getting notes data');
    } finally {
      setIsNotesLoading(false);
    }
  };

  const handleAddNote = async (value: string) => {
    if (focussedRowForNoteAdd === null) return false;
    const gfc = editData[focussedRowForNoteAdd].GFC;
    const cot = editData[focussedRowForNoteAdd].COT;
    const SellingCategory = editData[focussedRowForNoteAdd].SellingCategory;

    let data: notesInterface[] = [];
    try {
      setIsNoteSaving(true);
      if (value?.trim() === '') {
        toast.warning('Cannot save an empty note!!');
      } else {
        const payload = {
          gfc: gfc,
          note: value,
          planning_type: 'salesplan',
        };

        data = await tmsScreenServices.saveGFCNote(payload);

        const recentNote = data.sort((a, b) => b.ID - a.ID)[0];
        setLatestNote(recentNote);

        const tempSet = new Set(GFCNotesSet);
        tempSet.add(`${gfc}-${cot}-${SellingCategory}`);
        setGFCNotesSet(tempSet);

        const tempEditedError = new Set(gfcCotSellingCategoryError);
        tempEditedError.delete(`${gfc}-${cot}-${SellingCategory}`);
        setgfcCotSellingCategoryError(tempEditedError);
      }
      return true;
    } catch (_error) {
      console.log('Error saving the note');
      toast.error('Something went wrong!!');
    } finally {
      setNotesData(data);
      setIsNoteSaving(false);
    }
    return false;
  };

  const handleEditNote = async (id: number, note: string) => {
    try {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, true);
        } else {
          temp.set(id, true);
        }
        return temp;
      });
      const data = await tmsScreenServices.updateNote(id, note);
      if (data.status && notesData) {
        notesData.map(item => {
          if (item.ID === id) {
            item.CreatedDate = convertIsoToDateString(data.lastUpdatedDate) || '';
          }
        });
        toast.success('Note Updated successfully.');
      }
    } catch (_error) {
      console.log('handleEditNote=> ', _error);
      toast.error('Something went wrong.');
    } finally {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, false);
        } else {
          if (temp.get(id)) {
            temp.set(id, false);
          }
        }
        return temp;
      });
    }
  };

  const handleDeleteNote = async (id: number) => {
    if (focussedRowForNoteAdd === null) return;
    const deletedNote = notesData?.filter(item => item.ID === id)[0];
    const editedRow = editData[focussedRowForNoteAdd];

    const gfc = editedRow.GFC;
    const cot = editedRow.COT;
    const sellCat = editedRow.SellingCategory;

    try {
      setIsNoteDeleting(true);
      const data = await tmsScreenServices.deleteNote(id);
      if (data) {
        toast.success('Note deleted successfully.');
        if (notesData) {
          // removing the deleted note from the state in the application after successful deletion.
          const notes = notesData.filter(note => {
            if (note.ID !== id) {
              return note;
            }
          });

          setNotesData([...notes]);
        }
      }
    } catch (_error) {
      console.log('handleEditNote=> ', _error);
      toast.error('Something went wrong.');
    } finally {
      if (GFCNotesSet.has(`${gfc}-${cot}-${sellCat}-${deletedNote?.CreatedDate}`)) {
        GFCNotesSet.delete(`${gfc}-${cot}-${sellCat}-${deletedNote?.CreatedDate}`);
      }
      setIsNoteDeleting(false);
      setIsDeleteClicked(false);
    }
  };

  const getForecastValue = (rowIndex: number, monthDateString: string) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return '-';
    }
    const gfcCotKey = getGFCCOTKey(rowIndex);
    const selectedType = salesPlanTypeSelection.get(gfcCotKey) || 'current';
    return getSalesPlanValueByType(rowIndex, monthDateString, selectedType);
  };

  const isNumeric = (value: string) => {
    return /^[0-9]{1,9}$/.test(value);
  };

  const checkInqualityForCopyEditData = (
    rowData: salesPlanBulkEditDataInterfaceDataInstanceInterface[],
    copyRowData: salesPlanBulkEditDataInterfaceDataInstanceInterface[],
    type: SalesPlanType = 'current'
  ) => {
    for (let i = 0; i < rowData.length; i++) {
      if (type === 'saved') {
        if (rowData[i].SavedAdjustments.toString() !== copyRowData[i].SavedAdjustments.toString()) {
          return true;
        }
      } else if (type === 'old') {
        if (rowData[i].OldAdjustments.toString() !== copyRowData[i].OldAdjustments.toString()) {
          return true;
        }
      } else {
        if (
          rowData[i].AdjustedMarketForecast.toString() !==
          copyRowData[i].AdjustedMarketForecast.toString()
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const handleSaveInputValue = (idx: number, period: string, adjustment: string) => {
    if (adjustment[0] === '0') {
      adjustment = adjustment.replace(/^0+/, '');
    }
    if (adjustment === '' || adjustment === '-') {
      adjustment = '0';
    }
    if (!isNumeric(adjustment)) {
      return;
    }

    const tempEditData = deepCopy(editData);
    const rowData = tempEditData[idx].data;
    const gfcCotKey = getGFCCOTKey(idx);
    const selectedType = salesPlanTypeSelection.get(gfcCotKey) || 'current';
    let rowKey = '';

    for (let i = 0; i < rowData.length; i++) {
      if (rowData[i].PlanningMonth === period) {
        rowKey = `${rowData[i].GFC}-${rowData[i].COT}-${rowData[i].SellingCategory}`;
        switch (selectedType) {
          case 'current':
            rowData[i].AdjustedMarketForecast = Number(adjustment) || 0;
            break;
          case 'saved':
            if (rowData[i].isSavedSalesAvailable) {
              rowData[i].SavedAdjustments = Number(adjustment) || 0;
            }
            break;
          case 'old':
            if (rowData[i].isOldSalesAvailable) {
              rowData[i].OldAdjustments = Number(adjustment) || 0;
            }
            break;
          default:
            rowData[i].AdjustedMarketForecast = Number(adjustment) || 0;
        }
      }
    }

    if (rowKey) {
      const hasChanges = checkInqualityForCopyEditData(rowData, editDataCopy[idx].data, selectedType);
      if (!hasChanges) {
        setGFCEditedSet(prev => {
          const next = new Set(prev);
          next.delete(rowKey);
          return next;
        });
        setgfcCotSellingCategoryError(prev => {
          const temp = new Set(prev);
          temp.delete(rowKey);
          return temp;
        });
      } else {
        setGFCEditedSet(prev => {
          const next = new Set(prev);
          next.add(rowKey);
          return next;
        });
      }
    }

    setEditData(tempEditData);
  };

  const isSaveButtonEnabled = (idx: number) => {
    if (idx < 0 || idx >= editData.length || !editData[idx]) {
      return false;
    }
    const rowData = editData[idx];
    const rowKey = `${rowData.GFC}-${rowData.COT}-${rowData.SellingCategory}`;
    return GFCEditedSet.has(rowKey);
  };

  const isSaveAllBtnEnabled = () => {
    // Check if any row has save button enabled
    for (let i = 0; i < editData.length; i++) {
      if (isSaveButtonEnabled(i)) {
        return true;
      }
    }
    return false;
  };

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  const checkGfcCotSCisSaving = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return false;
    }
    const data = editData[rowIndex];
    return gfcCotSellingCategorySaveLoading.has(`${data.GFC}-${data.COT}-${data.SellingCategory}`);
  };

  const isNotesButtonEnabled = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return false;
    }
    const rowData = editData[rowIndex];
    const rowKey = `${rowData.GFC}-${rowData.COT}-${rowData.SellingCategory}`;
    return GFCEditedSet.has(rowKey);
  };

  const handleNoteButtonClick = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return;
    }
    getNotesData(editData[rowIndex].GFC, 'salesplan');
    setFocussedRowForNoteAdd(rowIndex);
  };

  const handleSingleSaveButtonClick = (index: number) => {
    setFocussedRowForSaving(index);
    setSingleSaveConfirmationModalVisibility(true);
  };

  const handleAllSaveButtonClick = () => {
    // setFocussedRowForSaving(index);
    setSaveAllConfirmationModalVisibility(true);
  };

  const markTheRowsSaved = (rowIndex: number[]) => {
    try {
      const tempEditData = deepCopy(editData);
      const tempSelectedType = new Map(salesPlanTypeSelection);
      const tempEditDataCopy = deepCopy(editDataCopy);
      rowIndex.forEach(index => {
        const data = tempEditData[index].data;
        const gfcCotKey = getGFCCOTKey(index);
        const initialData = tempEditDataCopy[index].data;
        const selectedType = salesPlanTypeSelection.get(gfcCotKey) || 'current';

        tempSelectedType.set(gfcCotKey, 'saved');
        tempEditData[index].isSavedSalesAvailable = true;
        tempEditDataCopy[index].isSavedSalesAvailable = true;

        data.forEach((element, index) => {
          let salesPlan: number = 0;
          element.isSavedSalesAvailable = true;
          initialData[index].isSavedSalesAvailable = true;
          // Get the value based on selected sales plan type
          switch (selectedType) {
            case 'current':
              salesPlan = Number(element.AdjustedMarketForecast);
              element.SavedAdjustments = salesPlan;
              initialData[index].SavedAdjustments = salesPlan;
              break;
            case 'saved':
              salesPlan = element.isSavedSalesAvailable ? Number(element.SavedAdjustments) : 0;
              element.SavedAdjustments = salesPlan;
              initialData[index].SavedAdjustments = salesPlan;
              break;
            case 'old':
              salesPlan = element.isOldSalesAvailable ? Number(element.OldAdjustments) : 0;
              element.SavedAdjustments = salesPlan;
              initialData[index].SavedAdjustments = salesPlan;
              break;
            default:
              salesPlan = Number(element.AdjustedMarketForecast);
              element.SavedAdjustments = salesPlan;
              initialData[index].SavedAdjustments = salesPlan;
          }
        });
      });

      setEditData(tempEditData);
      setEditDataCopy(tempEditDataCopy);
      setSalesPlanTypeSelection(tempSelectedType);
    } catch (_error) {
      console.log('Error while marking the rows saved', _error);
    }
  };

  const handleSingleSave = async () => {
    if (focussedRowForSaving === null) return;
    const data = editData[focussedRowForSaving];
    const gfcCotKey = getGFCCOTKey(focussedRowForSaving);
    const selectedType = salesPlanTypeSelection.get(gfcCotKey) || 'current';
    const rowKey = `${data.GFC}-${data.COT}-${data.SellingCategory}`;

    // Proceed with save
    // remove the gfc cot selling category from the error set
    const tempSet = new Set(gfcCotSellingCategoryError);
    tempSet.delete(rowKey);
    setgfcCotSellingCategoryError(tempSet);

    // save the ndc data
    const payload: {
      MarketShareAdjustment: number;
      Month: string;
      Adjustment: number;
      SalesPlan: number;
      Note: string;
      PlanningPeriod: string;
      MarketSalesForecast: string;
    }[] = [];
    const planning_period = getCurrentDate();

    data.data.forEach(element => {
      let euforecast = 0;
      let adjustment = 0;

      // Get the value based on selected sales plan type
      let adjustmentValue = 0;
      switch (selectedType) {
        case 'current':
          adjustmentValue = element.AdjustedMarketForecast;
          break;
        case 'saved':
          adjustmentValue = element.isSavedSalesAvailable
            ? element.SavedAdjustments
            : element.AdjustedMarketForecast;
          break;
        case 'old':
          adjustmentValue = element.isOldSalesAvailable
            ? element.OldAdjustments
            : element.AdjustedMarketForecast;
          break;
        default:
          adjustmentValue = element.AdjustedMarketForecast;
      }

      // EU Sales Plan = MS Plan % × the sales-plan adjustment the user entered
      // (not the raw market forecast, which is 0 for concepts without an active forecast).
      adjustment = adjustmentValue;
      euforecast = Math.ceil(adjustment * Number(element.MarketShareAdjustment));

      payload.push({
        MarketShareAdjustment: element.MarketShareAdjustmentID,
        Month: element.PlanningMonth,
        Adjustment: adjustment - element.ActiveMarketForecasts,
        SalesPlan: euforecast,
        Note: 'sales plan',
        PlanningPeriod: planning_period,
        MarketSalesForecast: String(element.ActiveMarketForecasts),
      });
    });

    try {
      //  Add the data in the loading set
      const focussedRowData = editData[focussedRowForSaving];
      const loadingSet = new Set(gfcCotSellingCategorySaveLoading);
      loadingSet.add(
        `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
      );
      setGfcCotSellingCategorySaveLoading(loadingSet);

      const data = await tmsScreenServices.updateSalesPlanData(payload);
      if (data) {
        const tempCopyData = deepCopy(editDataCopy);
        tempCopyData[focussedRowForSaving] = editData[focussedRowForSaving];
        setEditDataCopy(tempCopyData);

        const tempSet = new Set(GFCEditedSet);
        tempSet.delete(
          `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
        );
        setGFCEditedSet(tempSet);

        const tempNoteSet = new Set(GFCNotesSet);
        tempNoteSet.delete(
          `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
        );
        setGFCNotesSet(tempNoteSet);

        setSingleSaveConfirmationModalVisibility(false);
        setFocussedRowForNoteAdd(null);

        const tempRowsWithoutSaveSalesPlan = new Set(rowsWithoutSaveSalesPlan);
        tempRowsWithoutSaveSalesPlan.delete(
          `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
        );
        setRowsWithoutSaveSalesPlan(tempRowsWithoutSaveSalesPlan);
      }
      toast.success('Updated Successfully!');
      markTheRowsSaved([focussedRowForSaving]);
    } catch (_error) {
      console.log(`Error while saving the Sales plan`, _error);
      toast.error('Something went wrong while saving Sales Plan');
    } finally {
      const focussedRowData = editData[focussedRowForSaving];
      const loadingSet = new Set(gfcCotSellingCategorySaveLoading);
      loadingSet.delete(
        `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
      );
      setGfcCotSellingCategorySaveLoading(loadingSet);
      setGFCNotesSet(new Set());
    }
  };

  const handleSaveAllEdited = async () => {
    const rowsToSave: number[] = [];

    for (let i = 0; i < editData.length; i++) {
      const rowData = editData[i];
      const initialRowData = editDataCopy[i];

      // Determine if this row should be included in payload
      let shouldIncludeRow = false;

      if (!rowData.isSavedSalesAvailable) {
        // No saved values - always include
        shouldIncludeRow = true;
      } else {
        // Has saved values - check if any month differs from initial
        for (let j = 0; j < rowData.data.length; j++) {
          const currentData = rowData.data[j];
          const initialData = initialRowData?.data[j];

          if (currentData.isSavedSalesAvailable && initialData?.isSavedSalesAvailable) {
            if (Math.abs(currentData.SavedAdjustments - initialData.SavedAdjustments) > 0.001) {
              shouldIncludeRow = true;
              break;
            }
          }
        }
      }

      if (shouldIncludeRow) {
        rowsToSave.push(i);
      }
    }

    // Clear errors since all requirements are met
    setgfcCotSellingCategoryError(new Set());
    setIsSaveloading(true);

    // Create payload for qualifying rows
    const payload: {
      MarketShareAdjustment: number;
      Month: string;
      Adjustment: number;
      SalesPlan: number;
      Note: string;
      PlanningPeriod: string;
      MarketSalesForecast: string;
    }[] = [];
    const planning_period = getCurrentDate();

    for (const rowIndex of rowsToSave) {
      const data = editData[rowIndex];
      const gfcCotKey = getGFCCOTKey(rowIndex);
      const selectedType = salesPlanTypeSelection.get(gfcCotKey) || 'current';

      data.data.forEach(element => {
        let euforecast = 0;
        let adjustment = 0;

        // Get the value based on selected sales plan type
        let adjustmentValue = 0;
        switch (selectedType) {
          case 'current':
            adjustmentValue = element.AdjustedMarketForecast;
            break;
          case 'saved':
            adjustmentValue = element.isSavedSalesAvailable
              ? element.SavedAdjustments
              : element.AdjustedMarketForecast;
            break;
          case 'old':
            adjustmentValue = element.isOldSalesAvailable
              ? element.OldAdjustments
              : element.AdjustedMarketForecast;
            break;
          default:
            adjustmentValue = element.AdjustedMarketForecast;
        }

        // EU Sales Plan = MS Plan % × the sales-plan adjustment the user entered
        // (not the raw market forecast, which is 0 for concepts without an active forecast).
        adjustment = adjustmentValue;
        euforecast = Math.ceil(adjustment * Number(element.MarketShareAdjustment));

        payload.push({
          MarketShareAdjustment: element.MarketShareAdjustmentID,
          Month: element.PlanningMonth,
          Adjustment: adjustment - element.ActiveMarketForecasts,
          SalesPlan: euforecast,
          Note: 'sales plan',
          PlanningPeriod: planning_period,
          MarketSalesForecast: String(element.ActiveMarketForecasts),
        });
      });
    }

    try {
      const data = await tmsScreenServices.updateSalesPlanData(payload);
      if (data) {
        setEditDataCopy(deepCopy(editData));
        setGFCEditedSet(new Set());
        setgfcCotSellingCategoryError(new Set());
        setSaveAllConfirmationModalVisibility(false);
      }
      const tempRowsWithoutSaveSalesPlan = new Set(rowsWithoutSaveSalesPlan);
      tempRowsWithoutSaveSalesPlan.clear();
      setRowsWithoutSaveSalesPlan(tempRowsWithoutSaveSalesPlan);
      toast.success('Updated Successfully!');
      markTheRowsSaved(rowsToSave);
    } catch (_error) {
      console.log(`Error while saving the Sales plan`, _error);
      toast.error('Something went wrong while saving Sales Plan');
    } finally {
      setIsSaveloading(false);
      setGFCNotesSet(new Set());
    }
  };

  const getNextPageData = async () => {
    try {
      setNextPageLoading(true);
      const data = await tmsScreenServices.getFilteredDataForBulkEditSalesPlan({
        page_no: pageNumber,
        filters: filterData,
      });
      if (data.length === 0) {
        setHasMore(false);
        return;
      }
      setPageNumber(pageNumber + 1);
      if (data) {
        const currentLength = editData.length;
        setEditData([...editData, ...data]);
        setEditDataCopy(deepCopy([...editDataCopy, ...data]));

        // Initialize sales plan types for newly added rows
        data.forEach((_, index) => {
          initializeSalesPlanTypeForRow(currentLength + index);
        });

        const tempSavedSalesPlanSet = new Set(rowsWithoutSaveSalesPlan);
        for (let i = 0; i < data.length; i++) {
          if (!data[i].isSavedSalesAvailable) {
            tempSavedSalesPlanSet.add(`${data[i].GFC}-${data[i].COT}-${data[i].SellingCategory}`);
          }
        }
        setRowsWithoutSaveSalesPlan(tempSavedSalesPlanSet);
      }
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      setNextPageLoading(false);
      setInitialLoading(false);
    }
  };

  const loadMore = async () => {
    if (nextPageLoading || !hasMore) return;
    setNextPageLoading(true);
    getNextPageData();
  };

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const isRowExpanded = (rowIndex: number): boolean => {
    return expandedRows.has(rowIndex);
  };

  const getActiveMarketForecastValue = (rowIndex: number, monthDateString: string): string => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return '-';
    }
    const data = editData[rowIndex].data;
    for (let i = 0; i < data.length; i++) {
      if (data[i].PlanningMonth === monthDateString) {
        return Math.round(data[i].ActiveMarketForecasts).toString();
      }
    }
    return '-';
  };

  const handleApplyFilter = (newFilterData: msPlanBulkEditFilterInterface) => {
    setEditData([]);
    setEditDataCopy([]);
    setGfcCotSellingCategorySaveLoading(new Set());
    setGFCEditedSet(new Set());
    setGFCNotesSet(new Set());
    setRowsWithoutSaveSalesPlan(new Set());
    setExpandedRows(new Set()); // Reset expanded rows
    setSalesPlanTypeSelection(new Map()); // Reset sales plan type selections
    setFilterData(newFilterData);
    setHasMore(true);
    setPageNumber(0);
  };

  const _getInitialData = async () => {
    try {
      setIsDataLoading(true);
      const data = await tmsScreenServices.getFilteredDataForBulkEditSalesPlan({
        page_no: 0,
        filters: filterData,
      });
      setPageNumber(1); // Set to 1 since we just loaded page 0
      if (data.length === 0) {
        setHasMore(false);
        return;
      }
      if (data) {
        setEditData(data);
        setEditDataCopy(deepCopy(data));

        // Initialize sales plan types for all rows
        data.forEach((_, index) => {
          initializeSalesPlanTypeForRow(index);
        });
      }
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      setIsDataLoading(false);
    }
  };

  const toggleVisibilityForNotes = () => {
    setFocussedRowForNoteAdd(null);
    setNotesData([]);
  };

  const checkIfRowHasError = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return false;
    }
    const data = editData[rowIndex];
    return gfcCotSellingCategoryError.has(`${data.GFC}-${data.COT}-${data.SellingCategory}`);
  };

  const handleGoToMsPlan = () => {
    navigate('/fdp/target_market/bulk_edit/msplan');
    toggleVisibilityForRouteConfirmationPopUp();
  };

  const handleMsPlanButtonClick = () => {
    if (GFCEditedSet.size == 0) {
      handleGoToMsPlan();
    } else {
      setIsRouteChangeConfirmationPopUpVisible(true);
    }
  };

  const toggleVisibilityForRouteConfirmationPopUp = () => {
    setIsRouteChangeConfirmationPopUpVisible(false);
  };

  const handleGoToTMSViewScreen = () => {
    navigate('/fdp/target_market');
    setisCancelConfirmationPopVisible(false);
  };

  const handleCancelButtonClick = () => {
    if (GFCEditedSet.size == 0) {
      handleGoToTMSViewScreen();
    } else {
      setisCancelConfirmationPopVisible(true);
    }
  };

  const toggleCancelConfirmationPopUpVisibility = () => {
    setisCancelConfirmationPopVisible(!isCancelConfirmationPopVisible);
  };

  const handleCopy = (idx: number, month: string) => {
    const tempData = deepCopy(editData);
    const gfcCotKey = getGFCCOTKey(idx);
    const selectedType = salesPlanTypeSelection.get(gfcCotKey) || 'current';

    // Get the value based on selected type
    let sourceValue: number;
    const monthData = editData[idx].data.filter(item => item.PlanningMonth === month)[0];

    switch (selectedType) {
      case 'current':
        sourceValue = monthData.AdjustedMarketForecast;
        break;
      case 'saved':
        sourceValue = monthData.isSavedSalesAvailable
          ? monthData.SavedAdjustments
          : monthData.AdjustedMarketForecast;
        break;
      case 'old':
        sourceValue = monthData.isOldSalesAvailable
          ? monthData.OldAdjustments
          : monthData.AdjustedMarketForecast;
        break;
      default:
        sourceValue = monthData.AdjustedMarketForecast;
    }

    const forecastDate = new Date(month);

    // copying all the data in cells
    for (const item of tempData[idx].data) {
      if (new Date(item.PlanningMonth) > forecastDate) {
        // Update the appropriate field based on selected type
        switch (selectedType) {
          case 'current':
            item.AdjustedMarketForecast = Number(sourceValue) || 0;
            break;
          case 'saved':
            if (item.isSavedSalesAvailable) {
              item.SavedAdjustments = Number(sourceValue) || 0;
            }
            break;
          case 'old':
            if (item.isOldSalesAvailable) {
              item.OldAdjustments = Number(sourceValue) || 0;
            }
            break;
          default:
            item.AdjustedMarketForecast = Number(sourceValue) || 0;
        }
      }
    }

    const rowKey = `${tempData[idx].GFC}-${tempData[idx].COT}-${tempData[idx].SellingCategory}`;
    const hasChanges = checkInqualityForCopyEditData(tempData[idx].data, editDataCopy[idx].data, selectedType);

    if (!hasChanges) {
      setGFCEditedSet(prev => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
    } else {
      setGFCEditedSet(prev => {
        const next = new Set(prev);
        next.add(rowKey);
        return next;
      });
    }

    setEditData(tempData);
  };

  const handleConceptSort = (sortType: 'asc' | 'desc' | '') => {
    // Clear all existing data
    setEditData([]);
    setEditDataCopy([]);
    setGfcCotSellingCategorySaveLoading(new Set());
    setGFCEditedSet(new Set());
    setGFCNotesSet(new Set());
    setRowsWithoutSaveSalesPlan(new Set());
    setExpandedRows(new Set());
    setgfcCotSellingCategoryError(new Set());
    setSalesPlanTypeSelection(new Map()); // Reset sales plan type selections

    // Update filter data with new sort
    const newFilterData = {
      ...filterData,
      ConceptSort: sortType,
    };
    setFilterData(newFilterData);
    setHasMore(true);
    setPageNumber(0);

    // Fetch data with new sort immediately
    fetchDataWithSort(newFilterData);
  };

  const fetchDataWithSort = async (filterDataWithSort: msPlanBulkEditFilterInterface) => {
    try {
      setIsDataLoading(true);
      setInitialLoading(true);
      const data = await tmsScreenServices.getFilteredDataForBulkEditSalesPlan({
        page_no: 0,
        filters: filterDataWithSort,
      });
      setPageNumber(1); // Set to 1 since we just loaded page 0
      if (data.length === 0) {
        setHasMore(false);
        return;
      }
      if (data) {
        setEditData(data);
        setEditDataCopy(deepCopy(data));

        // Initialize sales plan types for all rows
        data.forEach((_, index) => {
          initializeSalesPlanTypeForRow(index);
        });

        const tempSavedSalesPlanSet = new Set(rowsWithoutSaveSalesPlan);
        for (let i = 0; i < data.length; i++) {
          if (!data[i].isSavedSalesAvailable) {
            tempSavedSalesPlanSet.add(`${data[i].GFC}-${data[i].COT}-${data[i].SellingCategory}`);
          }
        }
        setRowsWithoutSaveSalesPlan(tempSavedSalesPlanSet);
      }
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      setIsDataLoading(false);
      setInitialLoading(false);
    }
  };

  // useEffect(() => {
  //   getInitialData();
  // }, []);

  // useEffect(() => {
  //   getInitialData();
  // }, [filterData]);

  return {
    editData,
    setEditData,
    setEditDataCopy,
    handleApplyFilter,
    filterData,
    pageNumber,
    setPageNumber,
    isDataLoading,
    setIsDataLoading,
    getForecastValue,
    GFCNotesSet,
    setGFCNotesSet,
    gfcCotSellingCategoryError,
    setgfcCotSellingCategoryError,
    handleSingleSave,
    handleSaveAllEdited,
    gfcCotSellingCategorySaveLoading,
    isSaveAllLoading,
    isNotesLoading,
    notesData,
    setNotesData,
    isNoteSaving,
    getNotesData,
    handleAddNote,
    hasMore,
    nextPageLoading,
    loadMore,
    toggleVisibilityForNotes,
    handleSaveInputValue,
    isSaveAllBtnEnabled,
    checkGfcCotSCisSaving,
    isNotesButtonEnabled,
    handleNoteButtonClick,
    checkIfRowHasError,
    isSaveButtonEnabled,
    focussedRowForNoteAdd,
    singleSaveConfirmationModalVisibility,
    saveAllConfirmationModalVisibility,
    setSingleSaveConfirmationModalVisibility,
    setSaveAllConfirmationModalVisibility,
    focussedRowForSaving,
    handleSingleSaveButtonClick,
    handleAllSaveButtonClick,
    isRouteChangeConfirmationPopUpVisible,
    handleGoToMsPlan,
    handleMsPlanButtonClick,
    toggleVisibilityForRouteConfirmationPopUp,
    GFCEditedSet,
    isCancelConfirmationPopVisible,
    handleCancelButtonClick,
    toggleCancelConfirmationPopUpVisibility,
    handleGoToTMSViewScreen,
    isNoteEdited,
    setIsNoteEdited,
    isNoteDeleted,
    setIsNoteDeleted,
    handleCopy,
    handleEditNote,
    handleDeleteNote,
    deletingNote,
    isNoteDeleting,
    isDeleteClicked,
    setIsDeleteClicked,
    setDeletingNote,
    initialLoading,
    // New exports for accordion functionality
    expandedRows,
    toggleRowExpansion,
    isRowExpanded,
    getActiveMarketForecastValue,
    // New export for concept sorting
    handleConceptSort,
    // New exports for sales plan type selection
    salesPlanTypeSelection,
    getSelectedSalesPlanType,
    handleSalesPlanTypeChange,
    getSalesPlanValueByType,
    shouldShowToggle,
    getAvailableSalesPlanTypes,
    getDefaultSalesPlanType,
    isSalesPlanTypeChangeable,
    initializeSalesPlanTypeForRow,
  };
};
